import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import LandingPage from '@/pages/LandingPage';
import AuthCallback from '@/pages/AuthCallback';
import { AboutPage, TermsPage, PrivacyPage, KVKKPage } from '@/pages/LegalPage';

// Admin Pages
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminCampaigns from '@/pages/admin/AdminCampaigns';
import AdminTasks from '@/pages/admin/AdminTasks';
import AdminApprovals from '@/pages/admin/AdminApprovals';
import AdminContent from '@/pages/admin/AdminContent';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminUserDetail from '@/pages/admin/AdminUserDetail';
import AdminTaskReport from '@/pages/admin/AdminTaskReport';
import AdminCampaignReport from '@/pages/admin/AdminCampaignReport';
import AdminPoolModel from '@/pages/admin/AdminPoolModel';
import AdminAuditLogs from '@/pages/admin/AdminAuditLogs';
import AdminManagement from '@/pages/admin/AdminManagement';

import '@/App.css';

const ADMIN_ORIGIN = (process.env.REACT_APP_ADMIN_URL || 'https://admin.appstars.tech').replace(/\/+$/, '');
const PUBLIC_PRODUCTION_HOSTS = new Set(['appstars.tech', 'www.appstars.tech']);

const getHostname = () => window.location.hostname;

const getAdminHostname = () => {
  try {
    return new URL(ADMIN_ORIGIN).hostname;
  } catch {
    return 'admin.appstars.tech';
  }
};

const isAdminHost = () => getHostname() === getAdminHostname();
const isPublicProductionHost = () => PUBLIC_PRODUCTION_HOSTS.has(getHostname());

function FullPageRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
}

function PublicHostOnly({ children }) {
  if (isAdminHost()) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

function AdminHostOnly({ children }) {
  if (isPublicProductionHost()) {
    const destination = `${ADMIN_ORIGIN}${window.location.pathname}${window.location.search}${window.location.hash}`;
    return <FullPageRedirect to={destination} />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" />
        <Routes>
          {/* Public landing page */}
          <Route
            path="/"
            element={
              <PublicHostOnly>
                <LandingPage />
              </PublicHostOnly>
            }
          />
          
          {/* Auth Callback - handles Google sign-in redirects */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Admin Login Page - Desktop */}
          <Route
            path="/admin"
            element={
              <AdminHostOnly>
                <AdminLoginPage />
              </AdminHostOnly>
            }
          />
          
          {/* Legal Pages - Public URLs */}
          <Route path="/hakkimizda" element={<PublicHostOnly><AboutPage /></PublicHostOnly>} />
          <Route path="/kullanimkosullari" element={<PublicHostOnly><TermsPage /></PublicHostOnly>} />
          <Route path="/gizlilikpolitikasi" element={<PublicHostOnly><PrivacyPage /></PublicHostOnly>} />
          <Route path="/kvkk" element={<PublicHostOnly><KVKKPage /></PublicHostOnly>} />
          
          {/* Legacy URLs - redirect to new */}
          <Route path="/about" element={<PublicHostOnly><AboutPage /></PublicHostOnly>} />
          <Route path="/terms" element={<PublicHostOnly><TermsPage /></PublicHostOnly>} />
          <Route path="/privacy" element={<PublicHostOnly><PrivacyPage /></PublicHostOnly>} />
          <Route path="/kullanimsartlari" element={<PublicHostOnly><TermsPage /></PublicHostOnly>} />

          {/* Admin Protected Routes */}
          <Route
            path="/admin/*"
            element={
              <AdminHostOnly>
                <ProtectedRoute adminOnly>
                  <AdminLayout />
                </ProtectedRoute>
              </AdminHostOnly>
            }
          >
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="campaigns/:campaignId/report" element={<AdminCampaignReport />} />
            <Route path="tasks" element={<AdminTasks />} />
            <Route path="tasks/:taskId/report" element={<AdminTaskReport />} />
            <Route path="pool-model" element={<AdminPoolModel />} />
            <Route path="approvals" element={<AdminApprovals />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="admin-management" element={<AdminManagement />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to={isAdminHost() ? '/admin' : '/'} replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
